import { TRANSCRIPT_PENDING_MAX_AGE_MS } from '@consts';
import { TranscriptExpectation } from '@models/transcriptExpectations';
import { create } from 'zustand';

import { loadTranscriptExpectations, saveTranscriptExpectations } from '../localstorege_handler';

interface TranscriptExpectationsStore {
  expectations: Record<number, TranscriptExpectation>;
  hydrateExpectations: () => void;
  recordExpectation: (mediaitemId: number, expectation: TranscriptExpectation) => void;
  takeExpectation: (mediaitemId: number) => TranscriptExpectation | null;
  forgetExpectations: (mediaitemIds: number[]) => void;
  forgetExpectationsForTask: (taskEntityId: string) => void;
}

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const selectIsTranscriptPendingForTask =
  (taskEntityId: string) =>
  (state: TranscriptExpectationsStore): boolean =>
    Object.values(state.expectations).some(
      (expectation) =>
        expectation.taskEntityId === taskEntityId &&
        Date.now() - expectation.createdAt < TRANSCRIPT_PENDING_MAX_AGE_MS
    );

const withoutStale = (
  expectations: Record<number, TranscriptExpectation>
): Record<number, TranscriptExpectation> => {
  const cutoff = Date.now() - MAX_AGE_MS;
  return Object.fromEntries(
    Object.entries(expectations).filter(([, expectation]) => expectation.createdAt >= cutoff)
  );
};

export const useTranscriptExpectationsStore = create<TranscriptExpectationsStore>((set, get) => ({
  expectations: {},

  hydrateExpectations: () => {
    const expectations = withoutStale(loadTranscriptExpectations());
    set({ expectations: expectations });
    saveTranscriptExpectations(expectations);
  },

  recordExpectation: (mediaitemId, expectation) => {
    const expectations = { ...withoutStale(get().expectations), [mediaitemId]: expectation };
    set({ expectations: expectations });
    saveTranscriptExpectations(expectations);
  },

  forgetExpectations: (mediaitemIds) => {
    const fresh = withoutStale(get().expectations);
    const remaining = Object.fromEntries(
      Object.entries(fresh).filter(([id]) => !mediaitemIds.includes(Number(id)))
    );
    set({ expectations: remaining });
    saveTranscriptExpectations(remaining);
  },

  forgetExpectationsForTask: (taskEntityId) => {
    const remaining = Object.fromEntries(
      Object.entries(withoutStale(get().expectations)).filter(
        ([, expectation]) => expectation.taskEntityId !== taskEntityId
      )
    );
    set({ expectations: remaining });
    saveTranscriptExpectations(remaining);
  },

  takeExpectation: (mediaitemId) => {
    const fresh = withoutStale(get().expectations);
    const expectation = fresh[mediaitemId] ?? null;

    const { [mediaitemId]: _taken, ...rest } = fresh;
    set({ expectations: rest });
    saveTranscriptExpectations(rest);
    return expectation;
  }
}));
